import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  HospitalityKpi,
  HospitalityProperty,
  HospitalityReconciliation
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-kpi-dashboard',
  imports: [
    DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, SelectModule, TableModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="t('catalog.hospitality.kpi.title')" [description]="t('catalog.hospitality.kpi.description')">
      @if (loading() && !kpi()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <label class="date-field"><span>{{ t('catalog.hospitality.kpi.fromDate') }}</span><ain-date-input [(ngModel)]="fromDate" /></label>
            <label class="date-field"><span>{{ t('catalog.hospitality.kpi.toDate') }}</span><ain-date-input [(ngModel)]="toDate" /></label>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="t('catalog.hospitality.kpi.runReconciliation')" [disabled]="!selectedPropertyId || acting()" (click)="runReconciliation()"></button>
            <button pButton type="button" [label]="t('catalog.hospitality.kpi.seedDemo')" severity="secondary" [disabled]="acting()" (click)="seedDemo()"></button>
            <a routerLink="/hospitality/front-desk">{{ t('catalog.hospitality.frontDesk.title') }}</a>
          </div>

          @if (!selectedPropertyId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.kpi.selectProperty')" />
          } @else if (kpi()) {
            <div class="stats">
              <article><strong>{{ t('catalog.hospitality.kpi.occupancyRate') }}</strong><span>{{ kpi()!.occupancyRate | number:'1.1-1' }}%</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.adr') }}</strong><span>{{ kpi()!.averageDailyRate | number:'1.2-2' }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.revPar') }}</strong><span>{{ kpi()!.revenuePerAvailableRoom | number:'1.2-2' }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.occupiedRooms') }}</strong><span>{{ kpi()!.occupiedRooms }} / {{ kpi()!.totalRooms }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.activeStays') }}</strong><span>{{ kpi()!.activeStays }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.confirmedReservations') }}</strong><span>{{ kpi()!.confirmedReservations }}</span></article>
              <article class="warn"><strong>{{ t('catalog.hospitality.kpi.pendingHousekeeping') }}</strong><span>{{ kpi()!.pendingHousekeepingTasks }}</span></article>
            </div>

            <h4>{{ t('catalog.hospitality.kpi.reconciliationHistory') }}</h4>
            <p-table [value]="reconciliations()" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.hospitality.kpi.businessDate') }}</th>
                  <th>{{ t('catalog.hospitality.kpi.folioCharges') }}</th>
                  <th>{{ t('catalog.hospitality.kpi.prepayments') }}</th>
                  <th>{{ t('catalog.hospitality.kpi.refunds') }}</th>
                  <th>{{ t('catalog.hospitality.kpi.variance') }}</th>
                  <th>{{ t('catalog.tracking.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [class.warn-row]="Math.abs(row.postingVariance) > 0.01">
                  <td>{{ row.businessDate }}</td>
                  <td>{{ row.folioChargesTotal | number:'1.2-2' }}</td>
                  <td>{{ row.prepaymentsTotal | number:'1.2-2' }}</td>
                  <td>{{ row.refundsTotal | number:'1.2-2' }}</td>
                  <td>{{ row.postingVariance | number:'1.2-2' }}</td>
                  <td>{{ row.status }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; }
    .date-field { display: grid; gap: 4px; font-size: 0.85rem; }
    .stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
    .stats article { display: grid; gap: 4px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .stats article.warn { border-color: #fbbf24; background: #fffbeb; }
    .warn-row { background: #fffbeb; }
    @media (max-width: 960px) { .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  `]
})
export class HospitalityKpiDashboardComponent implements OnInit {
  readonly Math = Math;
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly kpi = signal<HospitalityKpi | null>(null);
  readonly reconciliations = signal<HospitalityReconciliation[]>([]);

  selectedPropertyId = '';
  fromDate = '';
  toDate = '';

  readonly propertyOptions = computed(() =>
    this.properties().map(p => ({ label: `${p.propertyCode} — ${p.name}`, value: p.id }))
  );

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        if (rows.length === 1) {
          this.selectedPropertyId = rows[0].id;
          this.load();
        } else {
          this.loading.set(false);
        }
      },
      error: () => this.loading.set(false)
    });
  }

  load(): void {
    if (!this.selectedPropertyId) return;
    this.loading.set(true);
    this.service.getHospitalityKpi(this.selectedPropertyId, this.fromDate || undefined, this.toDate || undefined).subscribe({
      next: kpi => {
        this.kpi.set(kpi);
        this.loading.set(false);
      },
      error: () => { this.kpi.set(null); this.loading.set(false); }
    });
    this.service.listHospitalityReconciliations(this.selectedPropertyId).subscribe({
      next: rows => this.reconciliations.set(rows),
      error: () => this.reconciliations.set([])
    });
  }

  runReconciliation(): void {
    if (!this.selectedPropertyId) return;
    this.acting.set(true);
    this.service.runHospitalityReconciliation({ propertyId: this.selectedPropertyId }).subscribe({
      next: () => { this.acting.set(false); this.load(); },
      error: () => this.acting.set(false)
    });
  }

  seedDemo(): void {
    this.acting.set(true);
    this.service.seedHospitalityDemo().subscribe({
      next: result => {
        this.acting.set(false);
        this.service.listHospitalityProperties().subscribe({
          next: rows => {
            this.properties.set(rows);
            this.selectedPropertyId = result.propertyId;
            this.load();
          }
        });
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
